import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LinearProgress,
  PrimaryButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@bb-ui/react-library/dist/components';
import { makeStyles, Theme } from '@bb-ui/react-library';
import { JobExecution } from '@lct-jobs/core';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { JobExecutionRow } from './JobExecutionRow';
import { ExecutionSearchField } from './ExecutionSearchField';
import { useStyles as useJobListStyles } from '../JobList';

const useStyles = makeStyles((theme: Theme) => ({
  emptyState: {
    padding: theme.spacing(3),
  },
}));

export interface ExecutionTableProps {
  /** The rows to render. Callers that filter pass the filtered subset. */
  executions: JobExecution[];
  loading: boolean;
  error?: Error;
  /**
   * Paging state of the underlying stream, which is deliberately separate from `executions`: a
   * filtered view with no matches yet still has to be able to page forward rather than dead-end
   * on an empty state.
   */
  nextPageToken?: string;
  onLoadMore: () => void;
  /** Already translated, so callers can vary it per filter. */
  emptyMessage: string;
  testIdPrefix: string;
  showOperationType?: boolean;
  showUser?: boolean;
  detailPathBuilder?: (execution: JobExecution) => string;
  /** Rendering the search box is opt-in: pass the handler and the field appears above the table. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchFieldId?: string;
  /** Shown above the rows when a search stopped before reaching the end of the stream. */
  searchNotice?: string;
}

export const ExecutionTable: React.FC<ExecutionTableProps> = ({
  executions,
  loading,
  error,
  nextPageToken,
  onLoadMore,
  emptyMessage,
  testIdPrefix,
  showOperationType,
  showUser,
  detailPathBuilder,
  searchValue,
  onSearchChange,
  searchFieldId,
  searchNotice,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { loadMoreButton } = useJobListStyles();

  const errorMessage = error ? (
    <ErrorMessage
      data-testid={`${testIdPrefix}-list-error`}
      title={t('jobService.loadError')}
      message={error.message}
      variant="block"
    />
  ) : null;

  // The field sits outside every content branch below, so a search that matched nothing can still
  // be cleared instead of trapping the user on an empty state.
  const searchField = onSearchChange ? (
    <ExecutionSearchField id={searchFieldId} value={searchValue ?? ''} onChange={onSearchChange} />
  ) : null;

  if (error && executions.length === 0) {
    return (
      <>
        {searchField}
        {errorMessage}
      </>
    );
  }

  if (loading && executions.length === 0) {
    return (
      <>
        {searchField}
        <LoadingIndicator data-testid={`${testIdPrefix}-list-init`} />
      </>
    );
  }

  let pager: React.ReactNode = null;
  if (nextPageToken) {
    pager = (
      <div className={loadMoreButton}>
        {loading ? (
          <LoadingIndicator data-testid="load-more-loading" />
        ) : (
          <PrimaryButton onClick={() => onLoadMore()}>{t('global.loadMore')}</PrimaryButton>
        )}
      </div>
    );
  } else if (executions.length > 0) {
    pager = (
      <Typography className={loadMoreButton} data-testid={`${testIdPrefix}-no-more-data`}>
        {t('jobService.noMoreData')}
      </Typography>
    );
  }

  return (
    <>
      {searchField}
      <TableContainer>
        {searchNotice ? (
          <Typography className={classes.emptyState} data-testid={`${testIdPrefix}-search-notice`}>
            {searchNotice}
          </Typography>
        ) : null}
        {errorMessage}
        {loading ? <LinearProgress ariaLabel="Loading Executions" /> : null}
        {executions.length === 0 ? (
          <Typography className={classes.emptyState} data-testid={`${testIdPrefix}-no-data`}>
            {emptyMessage}
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {showOperationType && (
                  <TableCell>{t('massQueryTooling.executions.typeColumn')}</TableCell>
                )}
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                {showUser && <TableCell>{t('massQueryTooling.executions.runByColumn')}</TableCell>}
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((execution) => (
                <JobExecutionRow
                  key={execution.executionId}
                  jobExecution={execution}
                  showOperationType={showOperationType}
                  showUser={showUser}
                  detailPath={detailPathBuilder?.(execution)}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {pager}
      </TableContainer>
    </>
  );
};

export default ExecutionTable;
